import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Clock, MapPin, Users, Plus,
  ChevronLeft, ChevronRight, Bell, Download, ExternalLink,
  School, Bus, X as XIcon, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * SCHOOL CALENDAR (BUILD 4B)
 * 
 * - Public events (optional)
 * - Internal schedules
 * - Field trips
 * - Closures
 * - PTA / parent nights
 */

const EVENT_TYPES = {
  class: { icon: School, color: 'var(--lk-earth-brown)', label: 'Class' },
  field_trip: { icon: Bus, color: 'var(--lk-secondary-orange)', label: 'Field Trip' },
  closure: { icon: XIcon, color: '#ef4444', label: 'Closure' },
  pta: { icon: Users, color: 'var(--lk-primary-green)', label: 'PTA/Parent Night' },
  event: { icon: Calendar, color: '#6366f1', label: 'Event' }
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function SchoolCalendar() {
  const navigate = useNavigate();
  const { institutionId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSteward, setIsSteward] = useState(false);
  const [schoolName, setSchoolName] = useState('');

  useEffect(() => {
    loadCalendar();
  }, [institutionId, currentDate.getMonth(), currentDate.getFullYear()]);

  const loadCalendar = async () => {
    try {
      const token = localStorage.getItem('token');
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await axios.get(
        `${API}/api/institutions/${institutionId}/calendar`,
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: {
            start: startOfMonth.toISOString(),
            end: endOfMonth.toISOString()
          }
        }
      );
      
      setEvents(response.data.events || []);
      setIsSteward(response.data.is_steward || false);
      setSchoolName(response.data.school_name || '');
    } catch (error) {
      console.error('Failed to load calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
    return events.filter(e => new Date(e.start_time).toDateString() === dateStr);
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const exportCalendar = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/institutions/${institutionId}/calendar/export`,
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${schoolName || 'school'}_calendar.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Calendar exported');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export calendar');
    }
  };

  const days = getDaysInMonth();
  const todayDay = new Date().getDate();
  const isCurrentMonth = currentDate.getMonth() === new Date().getMonth() && 
                         currentDate.getFullYear() === new Date().getFullYear();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>School Calendar</h1>
              {schoolName && (
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{schoolName}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={exportCalendar}
                className="rounded-full"
              >
                <Download className="w-4 h-4" />
              </Button>
              {isSteward && (
                <Button 
                  size="sm"
                  onClick={() => navigate(`/institutions/${institutionId}/calendar/create`)}
                  className="rounded-full"
                  style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Month Navigation */}
      <div className="px-4 py-4" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={previousMonth} className="p-2 rounded-full hover:bg-black/5">
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button onClick={nextMonth} className="p-2 rounded-full hover:bg-black/5">
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
        </div>
      </div>

      {/* Event Type Legend */}
      <div className="px-4 py-2 overflow-x-auto" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
        <div className="max-w-4xl mx-auto flex gap-3">
          {Object.entries(EVENT_TYPES).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: 'var(--fy-text-secondary)' }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
              {config.label}
            </div>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: 'var(--lk-earth-brown)' }} />
          </div>
        ) : (
          <>
            {/* Calendar Grid */}
            <Card className="p-4 mb-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              {/* Day Headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS.map(day => (
                  <div key={day} className="text-center text-xs font-medium py-2" style={{ color: 'var(--fy-text-disabled)' }}>
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Day Cells */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, idx) => {
                  const dayEvents = getEventsForDay(day);
                  const isSelected = selectedDate === day;
                  const isToday = isCurrentMonth && day === todayDay;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => day && setSelectedDate(day === selectedDate ? null : day)}
                      disabled={!day}
                      className={`aspect-square p-1 rounded-lg relative transition-all ${
                        day ? 'hover:bg-black/5 cursor-pointer' : ''
                      }`}
                      style={{ 
                        backgroundColor: isSelected ? 'var(--lk-earth-brown)' : 'transparent',
                        color: isSelected ? 'white' : 'var(--fy-text-primary)'
                      }}
                    >
                      {day && (
                        <>
                          <span className={`text-sm ${isToday && !isSelected ? 'font-bold' : ''}`}
                            style={{ color: isToday && !isSelected ? 'var(--lk-secondary-orange)' : undefined }}
                          >
                            {day}
                          </span>
                          {dayEvents.length > 0 && (
                            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                              {dayEvents.slice(0, 3).map((e, i) => (
                                <span 
                                  key={i}
                                  className="w-1.5 h-1.5 rounded-full"
                                  style={{ backgroundColor: EVENT_TYPES[e.event_type]?.color || 'var(--lk-primary-green)' }}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Selected Date Events */}
            {selectedDate && (
              <div className="space-y-3">
                <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
                  {MONTHS[currentDate.getMonth()]} {selectedDate}
                </h3>
                {getEventsForDay(selectedDate).length === 0 ? (
                  <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>No events on this day</p>
                ) : (
                  getEventsForDay(selectedDate).map(event => {
                    const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.event;
                    const TypeIcon = typeConfig.icon;
                    
                    return (
                      <Card 
                        key={event.event_id}
                        className="p-4"
                        style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                      >
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${typeConfig.color}20` }}
                          >
                            <TypeIcon className="w-5 h-5" style={{ color: typeConfig.color }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
                              >
                                {typeConfig.label}
                              </span>
                            </div>
                            <h4 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{event.title}</h4>
                            {event.description && (
                              <p className="text-sm mt-1" style={{ color: 'var(--fy-text-secondary)' }}>{event.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                {event.end_time && ` - ${new Date(event.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {event.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            )}

            {/* Upcoming Events (when no date selected) */}
            {!selectedDate && (
              <div className="space-y-3">
                <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Upcoming Events</h3>
                {events.filter(e => new Date(e.start_time) >= new Date()).slice(0, 5).map(event => {
                  const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.event;
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <Card 
                      key={event.event_id}
                      className="p-3 flex items-center gap-3"
                      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${typeConfig.color}20` }}
                      >
                        <TypeIcon className="w-5 h-5" style={{ color: typeConfig.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate" style={{ color: 'var(--fy-text-primary)' }}>{event.title}</p>
                        <p className="text-xs flex items-center gap-1" style={{ color: 'var(--fy-text-secondary)' }}>
                          <Clock className="w-3 h-3" />
                          {new Date(event.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
                      >
                        {typeConfig.label}
                      </span>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
